import React, { useRef } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  Avatar,
  Tooltip,
  Typography,
  ListItemText,
  CircularProgress,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { truncateText } from 'src/pages/chat/utils/text-formatting';
import { UserPresenceAvatarWrap } from 'src/pages/chat/components/common/avatar';
import { useGetUsersPresenceQuery } from 'src/pages/setting/components/users/slice/userApi';

import { Iconify } from 'src/components/iconify';
import { Field } from 'src/components/hook-form';
import { SearchNotFound } from 'src/components/search-not-found';

interface Option {
  lastName?: any;
  firstName?: any;
  email?: string;
  id: string;
  name: string;
  avatarUrl?: string;
  ProfileURL?: string;
  isUser?: boolean;
  assignedType?: string;
}

/**
 * Internal type: extends Option with a loader flag.
 */
type OptionWithLoader = Option & { __isLoader?: boolean };

interface MultiSelectAutocompleteProps {
  name: string;
  label: string;
  placeholder?: string;
  options: Option[];
  open: boolean;
  isFetching: boolean;
  value?: Option[];
  onChangeField?: ({ value, fieldName }: { value: any; fieldName: string }) => void;
  isOptionEqualToValue: (option: Option, value: Option) => boolean;
  searchValue: string;
  searchValueHandler: (value: string) => void;
  openListHandler: () => void;
  closeListHandler: () => void;
  listScrollHandler: (event: React.UIEvent<HTMLUListElement>) => void;
  fullWidth?: boolean;
  disabled?: boolean;
}

export const MultiSelectAutocomplete: React.FC<MultiSelectAutocompleteProps> = ({
  fullWidth,
  name,
  label,
  placeholder = 'Select',
  options,
  open,
  isFetching,
  value,
  onChangeField,
  isOptionEqualToValue,
  searchValue,
  searchValueHandler,
  openListHandler,
  closeListHandler,
  listScrollHandler,
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslate('translation');
  const listboxRef = useRef<HTMLUListElement | null>(null);

  const userIds = Array.from(
    new Set([
      ...(options || [])
        .filter((a: any) => a.isUser || a.assignedType === 'USER')
        .map((a: any) => a.id),
      ...(value || [])
        .filter((a: any) => a.isUser || a.assignedType === 'USER')
        .map((a: any) => a.id),
    ])
  );

  const { data: presenceMap = {} } = useGetUsersPresenceQuery(userIds, {
    skip: userIds.length === 0,
  });

  const renderName = (option: Option) => {
    const displayText =
      [option?.firstName, option?.lastName].filter(Boolean).join(' ').trim() ||
      option?.name?.trim() ||
      option?.email?.trim() ||
      '';

    if (displayText.length > 20) {
      return (
        <Tooltip title={displayText}>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {truncateText(displayText, 20)}
          </Typography>
        </Tooltip>
      );
    }

    return displayText;
  };

  // Create a loader pseudo-option that we append to the end when fetching more and there are already options.
  const loaderOption: OptionWithLoader = {
    id: '__loader__',
    name: '__loader__',
    __isLoader: true,
  };

  // Show loader item at bottom only when there are already options and fetching more.
  const combinedOptions: OptionWithLoader[] =
    isFetching && options.length > 0
      ? [...(options as OptionWithLoader[]), loaderOption]
      : (options as OptionWithLoader[]);

  // Safe wrapper for isOptionEqualToValue to avoid comparing loader item with real values
  const isOptionEqualSafe = (opt: OptionWithLoader, val: OptionWithLoader) => {
    if ((opt as any).__isLoader || (val as any).__isLoader) return false;
    return isOptionEqualToValue(opt as Option, val as Option);
  };

  return (
    <Field.Autocomplete
      fullWidth={fullWidth}
      name={name}
      label={label}
      disabled={disabled}
      limitTags={3}
      popupIcon={null}
      multiple
      disableCloseOnSelect
      // use our combined options (with loader appended when needed)
      options={combinedOptions}
      open={open}
      onChangeField={onChangeField}
      onOpen={openListHandler}
      onClose={closeListHandler}
      onInputChange={(e, newVal, reason) => {
        if (reason === 'input') searchValueHandler(newVal);
      }}
      // don't show the loader option in the input label
      getOptionLabel={(option: OptionWithLoader) =>
        option.__isLoader ? '' : (option?.name ?? option?.email ?? '')
      }
      isOptionEqualToValue={isOptionEqualSafe}
      placeholder={placeholder}
      // when there are zero options and it's fetching -> show centered loader
      noOptionsText={
        isFetching && options.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <SearchNotFound query={searchValue} />
        )
      }
      filterOptions={(x) => x} // keep original ordering, don't filter out loader
      slotProps={{
        listbox: {
          ref: listboxRef,
          onScroll: listScrollHandler,
          style: { maxHeight: 300, overflow: 'auto' }, // keep dropdown scrollable
        },
        textfield: {
          slotProps: { inputLabel: { shrink: true } },
          InputLabelProps: { shrink: true },
        },
      }}
      // renderOption: if the option is our loader pseudo-option, render a centered spinner row
      renderOption={(props, option: OptionWithLoader, { selected }) => {
        const { key, ...optionProps } = props as any;

        if ((option as any).__isLoader) {
          // make loader non-interactive
          return (
            <li key="__loader__" {...optionProps} style={{ pointerEvents: 'none' }}>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            </li>
          );
        }

        return (
          <li key={option.id} {...optionProps}>
            <UserPresenceAvatarWrap
              showPresence={option.isUser || option.assignedType === 'USER'}
              isOnline={!!presenceMap[option.id]}
              size="sm"
              sx={{ mr: 1 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  position: 'relative',
                }}
              >
                <Avatar
                  alt={option?.firstName || option?.lastName || option?.name || 'Unknown'}
                  src={option?.ProfileURL || option?.avatarUrl}
                  sx={{ width: 32, height: 32 }}
                >
                  {!option?.ProfileURL &&
                    `${option?.firstName?.[0] || option?.lastName?.[0] || option?.name?.[0] || ''}`}
                </Avatar>
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    width: 1,
                    height: 1,
                    opacity: selected ? 1 : 0,
                    display: 'flex',
                    position: 'absolute',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.grey[900],
                    color: 'primary.main',
                    borderRadius: '50%',
                    transition: 'opacity 150ms ease-in-out',
                  }}
                >
                  <Iconify icon="eva:checkmark-fill" />
                </Box>
              </Box>
            </UserPresenceAvatarWrap>

            <ListItemText
              primary={renderName(option)}
              secondary={option.isUser || option.assignedType === 'USER' ? t('USER') : t('TEAM')}
              sx={{ flex: 1, pr: 1 }}
              slotProps={{
                secondary: { sx: { typography: 'caption', color: 'text.disabled' } },
              }}
            />
          </li>
        );
      }}
      // ensure not to render loader as a tag if somehow selected (it won't be selectable but safe filter)
      renderTags={(selectedList, getTagProps) =>
        selectedList
          ?.filter((opt: OptionWithLoader) => !(opt as any).__isLoader)
          .map((option: OptionWithLoader, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                {...tagProps}
                key={option.id}
                label={renderName(option)}
                avatar={
                  <UserPresenceAvatarWrap
                    showPresence={option.isUser || option.assignedType === 'USER'}
                    isOnline={!!presenceMap[option.id]}
                    size="xs"
                    dotOutset
                  >
                    <Avatar
                      alt={option?.firstName || option?.lastName || option?.name || 'Unknown'}
                      src={option?.ProfileURL ?? option?.avatarUrl}
                      sx={{ width: 18, height: 18 }}
                    >
                      {!option?.ProfileURL &&
                        `${option?.firstName?.[0] || option?.lastName?.[0] || option?.name?.[0] || ''}`}
                    </Avatar>
                  </UserPresenceAvatarWrap>
                }
                size="small"
                variant="soft"
              />
            );
          })
      }
    />
  );
};
